'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function DolguIslemleriYuzLiftingiSaglarMiPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/dolgu-islemleri-yuz-liftingi-saglar-mi';
  const pageTitle = 'Dolgu İşlemleri Yüz Liftingi Sağlar mı? - Cerrahi Olmayan Yüz Gençleştirme';
  const pageDescription = 'Dolgu işlemleri yüz liftingi etkisi yaratır mı? Hangi bölgelerde uygulanır, ne kadar süre kalıcıdır? Cerrahi olmayan yüz gençleştirmenin bilimsel açıklaması.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Dolgu İşlemleri Yüz Liftingi Sağlar mı?', href: '/tr/blog/dolgu-islemleri-yuz-liftingi-saglar-mi' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Dolgu işlemleri ve yüz liftingi etkisi değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-pink-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Dolgu & Yüz Liftingi
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Dolgu İşlemleri Yüz Liftingi Sağlar mı?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>24 Ocak 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>9 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Dolgu (filler) uygulamaları, ciltteki hacim kaybını gidermek ve yüz hatlarını yeniden şekillendirmek için en sık tercih edilen estetik işlemlerden biridir. Ancak pek çok kişi şu soruyu merak eder: "Dolgu işlemleri yüz liftingi kadar etkili midir?" Kısaca yanıt: Dolgu, tam bir cerrahi lifting sağlamaz; ancak yüzün yapısal desteğini güçlendirerek "mini lifting" etkisi oluşturabilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Dolgu İşlemi Nedir?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dolgu işlemleri, cilt altına hyaluronik asit, kalsiyum hidroksiapatit veya polilaktik asit gibi maddelerin enjekte edilmesiyle yapılır. Amaç, ciltteki hacim kaybını telafi etmek ve daha genç, dinamik bir görünüm elde etmektir.
              </p>
              
              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Dolgu maddeleri şunlardır:</h3>
                <ul className="list-disc list-inside text-blue-800 space-y-2">
                  <li><strong>Hyaluronik Asit:</strong> En yaygın ve güvenli dolgu türüdür. Nem tutar, hacim kazandırır.</li>
                  <li><strong>Kalsiyum Hidroksiapatit:</strong> Kemik hattı belirginleştirmek için kullanılır (çene, elmacık).</li>
                  <li><strong>Polilaktik Asit:</strong> Kollajen üretimini uyarır, zamanla doğal dolgunluk sağlar.</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Dolgu Lifting Etkisini Nasıl Oluşturur?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Yüz yaş aldıkça yağ yastıkçıkları yer değiştirir, kemik yapılar hacim kaybeder. Dolgu, bu boşlukları doldurarak ve hacmi yeniden dengeleyerek cildi yukarı doğru destekler. Bu etki özellikle şu bölgelerde belirgindir:
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-green-800 space-y-2">
                  <li>Elmacık kemikleri</li>
                  <li>Nazolabial (burun-dudak) çizgiler</li>
                  <li>Çene hattı</li>
                  <li>Şakak bölgesi</li>
                </ul>
                <p className="text-green-800 mt-3 font-medium">
                  Dolgu işlemleri bu bölgelerde uygulandığında, yüz daha gergin ve dinamik görünür. Bu durum "cerrahi olmayan lifting" olarak tanımlanır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Dolgu ve Klasik Yüz Lifting Arasındaki Farklar
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Özellik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Dolgu Uygulaması
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Cerrahi Yüz Lifting
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Yöntem</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Enjeksiyonla uygulanır</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Cerrahi kesilerle yapılır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">İyileşme Süresi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hemen sosyal hayata dönüş</td>
                      <td className="px-6 py-4 text-sm text-gray-900">1–2 hafta istirahat</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Etkisi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Anında, 12–18 ay kalıcı</td>
                      <td className="px-6 py-4 text-sm text-gray-900">5–10 yıl kalıcı</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Risk</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Düşük (morarma, ödem)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Daha yüksek (yara izi, anestezi)</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Maliyet</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Orta düzey</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Yüksek</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-6 font-medium">
                Dolgu, ameliyatsız bir alternatif sunar; fakat cerrahi liftingin derin etkisini tamamen yerine geçirmez.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Etki Süresi ve Kalıcılık
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dolgu etkisi genellikle 12 ila 18 ay arasında sürer. Bu süreyi etkileyen faktörler:
              </p>

              <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-yellow-800 space-y-2">
                  <li>Kullanılan dolgu tipi ve markası</li>
                  <li>Enjeksiyon bölgesi</li>
                  <li>Kişisel metabolizma hızı</li>
                  <li>Mimik hareketleri ve yaşam tarzı</li>
                </ul>
                <p className="text-yellow-800 mt-3">
                  Düzenli bakım ve uygun seans aralıkları ile bu süre uzatılabilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Hangi Durumlarda Etkisi Sınırlı Olabilir?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Aşırı sarkma veya gevşek cilt (örneğin 55 yaş üstü hastalarda), derin kırışıklıklar ve fazla yağ kaybı, çok ince cilt yapısı gibi durumlarda tek başına dolgu yeterli olmayabilir; mezoterapi, PRP veya iple askı gibi yöntemlerle kombine edilmesi önerilir.
              </p>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Dolgu işlemleri ve yüz liftingi etkisi"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Dolgu yüzü şişirir mi?</h3>
                  <p className="text-gray-700">Hayır. Doğru miktarda ve doğru teknikle uygulandığında yüz hatlarını doğal biçimde destekler.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Dolgu işlemi sonrası hemen sonuç alınır mı?</h3>
                  <p className="text-gray-700">Evet, etki anında görülür. Hafif ödem 1–2 gün içinde azalır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Dolgu sonrası mimikler kaybolur mu?</h3>
                  <p className="text-gray-700">Hayır. Botokstan farklı olarak dolgu kas hareketlerini kısıtlamaz.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Dolgu ne kadar sürer?</h3>
                  <p className="text-gray-700">Ortalama 12–18 ay kalıcıdır; bazı kalıcı dolgu türleri 24 aya kadar etki gösterebilir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Dolgu işlemi yüzü gençleştirir mi?</h3>
                  <p className="text-gray-700">Evet. Ciltteki hacim kaybını gidererek lifting benzeri bir gençleşme sağlar.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>American Society for Dermatologic Surgery (ASDS) – "Filler Treatments and Face Lifting Effects"</li>
                <li>Türk Estetik Tıp Derneği (TETD) – Dolgu Uygulama Rehberi</li>
                <li>Harvard Health Publishing – "Non-Surgical Face Rejuvenation"</li>
                <li>Mayo Clinic – "Dermal Fillers: Uses and Safety"</li>
                <li>ISAPS (International Society of Aesthetic Plastic Surgery) – 2024 Global Aesthetic Survey</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#dolgu</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#yüzlifting</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#hyaluronikasit</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#ameliyatsızestetik</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#filler</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Dolgu Uygulaması İçin Uzman Değerlendirme</h3>
              <p className="mb-6">Yüz gençleştirme ve hacim kazandırma için dolgu uygulaması hakkında detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Dolgu uygulaması hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-blue-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/mezoterapi-etkileri-ne-zaman-gorulur"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Mezoterapi Etkileri Ne Zaman Görülür?</h4>
              <p className="text-gray-600 text-sm">Mezoterapi sonuçlarının görünme süresi ve kalıcılık hakkında bilgiler.</p>
            </Link>
            <Link
              href="/tr/blog/botoks-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Nedir? Nasıl Yapılır?</h4>
              <p className="text-gray-600 text-sm">Botoks uygulaması hakkında temel bilgiler ve süreç.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

